@if (!drawerMode) {
  <app-page-breadcrumb [pageTitle]="pageTitle"></app-page-breadcrumb>
}

<ng-container *ngTemplateOutlet="drawerMode ? formBody : pageWrapper"></ng-container>

<ng-template #pageWrapper>
  <app-component-card>
    <ng-container *ngTemplateOutlet="formBody"></ng-container>
  </app-component-card>
</ng-template>

<ng-template #formBody>
  <div [class.p-6]="drawerMode">
  @if (loading) {
    <div class="py-8 text-center text-gray-500 dark:text-gray-400">Cargando...</div>
  } @else {
    <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-4">

      @if (error) {
        <div class="p-3 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
          <p class="text-error-600 dark:text-error-400 text-sm">{{ error }}</p>
        </div>
      }

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo *</label>
          <select formControlName="templateType"
            class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            @for (t of types; track t) { <option [ngValue]="t">{{ typeLabel(t) }}</option> }
          </select>
          <!-- INC 80: decir cuándo se envía cada tipo. Varios se podían crear y no los
               leía nadie: el usuario personalizaba un correo que nunca salía. -->
          @if (tipoSeleccionado; as uso) {
            @if (uso.automatico) {
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ uso.cuando }}</p>
            } @else {
              <p class="mt-1 flex items-start gap-1.5 text-xs text-warning-600 dark:text-warning-400">
                <svg class="mt-0.5 h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                </svg>
                <span>{{ uso.cuando }} Puedes guardarla para tenerla lista, pero el sistema no la usará todavía.</span>
              </p>
            }
          }
        </div>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nombre *</label>
          <input type="text" formControlName="name"
            [class.border-error-500]="hasError('name')"
            class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
        </div>

        <div class="md:col-span-2">
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Asunto *</label>
          <input #subjectInput type="text" formControlName="subject"
            (blur)="rememberSubjectCaret()" (keyup)="rememberSubjectCaret()" (click)="rememberSubjectCaret()"
            [class.border-error-500]="hasError('subject')"
            class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          @if (allVariables.length) {
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              El asunto admite las mismas variables que el cuerpo — haz clic en cualquiera de la lista de abajo para insertarla aquí.
            </p>
          }
        </div>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Página de reserva (opcional)</label>
          <select formControlName="idBookingLink"
            class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            <option [ngValue]="null">Global del tenant</option>
            @for (link of bookingLinks; track link.id) {
              <option [ngValue]="link.id">{{ bookingLinkLabel(link) }}</option>
            }
          </select>
          <p class="text-xs text-gray-500 mt-1">Dejar en "Global del tenant" para aplicar a todas las páginas.</p>
        </div>

        <div class="flex items-center gap-4 pt-6">
          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" formControlName="isDefault" class="h-4 w-4 rounded border-gray-300 text-brand-500">
            Predeterminada
          </label>
          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" formControlName="isActive" class="h-4 w-4 rounded border-gray-300 text-brand-500">
            Activa
          </label>
        </div>
        <!-- INC 81: sin esta casilla la plantilla NO se envía. Antes daba igual marcarla —el
             sistema cogía cualquier plantilla activa del tipo, la más reciente— y el cliente
             veía salir un correo que nunca eligió. -->
        <p class="text-xs text-gray-500 dark:text-gray-400">
          Marca <strong>Predeterminada</strong> para que este sea el correo que se envía en este tipo.
          Si no marcas ninguna, se usa la plantilla predeterminada del sistema. Solo puede haber una
          predeterminada por tipo.
        </p>
      </div>

      <div>
        <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Cuerpo *</label>
        <app-rich-text-editor
          [value]="form.get('bodyHtml')?.value || ''"
          (valueChange)="form.get('bodyHtml')?.setValue($event)"
          [variableNamespaces]="variableNamespaces"
          [uploadHandler]="bodyImageUploadHandler"
          minHeight="260px"
          placeholder="Escribe el contenido del correo…"></app-rich-text-editor>
        @if (hasError('bodyHtml')) {
          <p class="mt-1 text-sm text-error-500">El cuerpo es obligatorio.</p>
        }
        <p class="text-xs text-gray-500 mt-1">
          Da formato al texto, inserta imágenes y variables desde la barra del editor.
        </p>
      </div>

      <!-- INC 65: referencia de variables. El botón {{ '{{ }}' }} del editor las inserta en el
           cuerpo; en el Asunto hay que escribirlas a mano, así que se listan aquí. -->
      @if (allVariables.length) {
        <details open class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <summary class="cursor-pointer px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300">
            Variables disponibles ({{ allVariables.length }}) — se reemplazan al enviar
          </summary>
          <div class="px-4 pb-4 pt-1">
            <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Se pueden usar tanto en el <strong>Asunto</strong> como en el <strong>Cuerpo</strong>.
              Haz clic en una variable para insertarla en el <strong>Asunto</strong>; en el cuerpo se
              insertan con el botón <span class="font-mono">{{ '{{ }}' }}</span> Variables del editor.
              Si la reunión no tiene ese dato, la variable se reemplaza por texto vacío.
            </p>
            <div class="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
              @for (v of allVariables; track v.key) {
                <button type="button" (click)="insertVariableEnAsunto(v.key)"
                  [title]="v.description || ''"
                  class="flex w-full items-start gap-2 rounded-md px-1.5 py-1 text-left text-xs hover:bg-white dark:hover:bg-gray-800">
                  <code class="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 font-mono text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">{{ '{{' + v.key + '}}' }}</code>
                  <span class="min-w-0 text-gray-600 dark:text-gray-400">
                    {{ v.label }}@if (v.sample) { <span class="text-gray-400 dark:text-gray-500"> — ej. {{ v.sample }}</span> }
                  </span>
                </button>
              }
            </div>
          </div>
        </details>
      }

      <div>
        <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Cuerpo texto plano (opcional)</label>
        <textarea formControlName="bodyText" rows="4"
          class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"></textarea>
      </div>

      <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <app-button variant="outline" type="button" (btnClick)="cancelar()">Cancelar</app-button>
        <app-button variant="primary" type="submit" [disabled]="saving || form.invalid">
          {{ saving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear') }}
        </app-button>
      </div>
    </form>
  }
  </div>
</ng-template>
